import api from '@/lib/api';
import { generateFields } from '@/api/generators-api';
import type {
  Schedule,
  CreateScheduleRequest,
  CreateWeeklyGridRequest,
} from '@/types/schedule.model';
import { SCHEDULE_FIELDS } from './schedule-fields.config';
import type { FieldDefinition } from '@/types/fields-api.model';

/**
 * Response type for paginated or wrapped responses
 */
interface ScheduleResponse {
  content?: Schedule[];
  data?: Schedule[];
  schedules?: Schedule[];
}

/**
 * Try to extract valid JSON array from a potentially malformed string
 * This handles cases where the API returns extra data after the JSON
 */
function extractJsonArray(str: string): string {
  // Find the first '[' and try to find its matching ']'
  const startIndex = str.indexOf('[');
  if (startIndex === -1) return str;
  
  let depth = 0;
  let endIndex = -1;
  
  for (let i = startIndex; i < str.length; i++) {
    if (str[i] === '[') depth++;
    if (str[i] === ']') depth--;
    if (depth === 0) {
      endIndex = i;
      break;
    }
  }
  
  if (endIndex !== -1) {
    return str.substring(startIndex, endIndex + 1);
  }
  
  return str;
}

/**
 * Normalize API response to always return an array of schedules
 * Handles cases where response might be a JSON string or already parsed
 */
function normalizeScheduleResponse(data: unknown): Schedule[] {
  let parsedData = data;
  
  // If data is a string, try to parse it as JSON
  if (typeof data === 'string') {
    try {
      // First try direct parse
      parsedData = JSON.parse(data);
    } catch {
      // If that fails, try to extract just the JSON array part
      try {
        const cleanedJson = extractJsonArray(data);
        parsedData = JSON.parse(cleanedJson);
      } catch (e2) {
        console.error('Failed to parse schedule response as JSON:', e2);
        return [];
      }
    }
  }
  
  // If it's already an array, return it
  if (Array.isArray(parsedData)) {
    return parsedData;
  }

  // If it's an object, check for common wrapper properties
  if (parsedData && typeof parsedData === 'object') {
    const response = parsedData as ScheduleResponse;
    
    if (Array.isArray(response.content)) {
      return response.content;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.schedules)) {
      return response.schedules;
    }
  }

  // If we can't find schedules, return empty array
  console.warn('Unexpected schedule API response format:', parsedData);
  return [];
}

/**
 * Get all schedules for a specific club
 * @param clubId - The club ID to get schedules for
 * @param fields - Field selector for the response (default: SCHEDULE_FIELDS.DEFAULT)
 * @returns Array of schedules for the club
 */
export const getSchedulesByClub = async (
  clubId: string,
  fields: FieldDefinition = SCHEDULE_FIELDS.DEFAULT
): Promise<Schedule[]> => {
  const response = await api.get(`/schedules/clubs/${clubId}`, {
    params: {
      fields: generateFields(fields),
    },
  });
  return normalizeScheduleResponse(response.data);
};

/**
 * Get a single schedule of a club
 * @param clubId - The club ID the schedule belongs to
 * @param scheduleId - The schedule ID
 * @param fields - Field selector for the response (default: SCHEDULE_FIELDS.DEFAULT)
 * @returns The schedule
 */
export const getScheduleByClub = async (
  clubId: string,
  scheduleId: string,
  fields: FieldDefinition = SCHEDULE_FIELDS.DEFAULT
): Promise<Schedule> => {
  const response = await api.get<Schedule>(`/schedules/clubs/${clubId}/${scheduleId}`, {
    params: {
      fields: generateFields(fields),
    },
  });
  return response.data;
};

/**
 * Create a new schedule for a specific club
 * @param clubId - The club ID to create the schedule for
 * @param scheduleData - The schedule data to create
 * @param fields - Field selector for the response (default: SCHEDULE_FIELDS.DEFAULT)
 * @returns The created schedule
 */
export const createSchedule = async (
  clubId: string,
  scheduleData: CreateScheduleRequest | CreateWeeklyGridRequest,
  fields: FieldDefinition = SCHEDULE_FIELDS.DEFAULT
): Promise<Schedule> => {
  const response = await api.post<Schedule>(
    `/schedules/clubs/${clubId}`,
    scheduleData,
    {
      params: {
        fields: generateFields(fields),
      },
    }
  );
  
  // Handle case where response might be a string
  if (typeof response.data === 'string') {
    try {
      return JSON.parse(response.data);
    } catch {
      const cleaned = extractJsonArray(response.data);
      return JSON.parse(cleaned);
    }
  }
  return response.data;
};

/**
 * Get all schedules for multiple clubs
 * @param clubIds - Array of club IDs to get schedules for
 * @param fields - Field selector for the response (default: SCHEDULE_FIELDS.DEFAULT)
 * @returns Array of schedules with club information
 */
export const getSchedulesForClubs = async (
  clubIds: string[],
  fields: FieldDefinition = SCHEDULE_FIELDS.DEFAULT
): Promise<{ clubId: string; schedules: Schedule[] }[]> => {
  const results = await Promise.all(
    clubIds.map(async (clubId) => {
      try {
        const schedules = await getSchedulesByClub(clubId, fields);
        return { clubId, schedules };
      } catch (error) {
        console.error(`Error fetching schedules for club ${clubId}:`, error);
        return { clubId, schedules: [] };
      }
    })
  );
  return results;
};

// Re-export fields config for convenience
export { SCHEDULE_FIELDS };
